import { useEffect, useRef, useState } from "react";
import { Mail, X } from "lucide-react";
import { COMPANY } from "@/data/company";

const FIELD =
  "w-full rounded-xl border border-[#DDECE9] bg-white px-3.5 py-2.5 text-[15px] text-[#0F172A] placeholder:text-[#94A3B8] outline-none transition-colors focus:border-[#22A08B] focus:ring-2 focus:ring-[#22A08B]/20";

export default function ContactModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const firstField = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && onClose();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    firstField.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const body = `${message.trim()}\n\n--\n${name.trim()}\n${email.trim()}`;
    const subject = `Message from ${name.trim()} via Rivyou`;
    // Opens the visitor's own mail app with the message pre-filled; nothing is sent from the site itself.
    window.location.href = `mailto:${COMPANY.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    onClose();
    setMessage("");
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#0b1511]/60 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-7"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-[#64748B] transition-colors hover:bg-[#F0F9F8] hover:text-[#0F172A]"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="mb-1 flex items-center gap-2 text-[#22A08B]">
          <Mail className="h-4 w-4" />
          <span className="text-xs font-bold uppercase tracking-wider">Contact us</span>
        </div>
        <h2 id="contact-modal-title" className="m-0 mb-1 text-2xl font-bold text-[#0F172A]">
          Send us a message
        </h2>
        <p className="m-0 mb-5 text-sm leading-relaxed text-[#475569]">
          Write your message and we&apos;ll open your email app with it ready to send to {COMPANY.email}.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5">
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-[#0F172A]">
            Your name
            <input
              ref={firstField}
              className={FIELD}
              type="text"
              required
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-[#0F172A]">
            Your email
            <input
              className={FIELD}
              type="email"
              required
              maxLength={200}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-[#0F172A]">
            Message
            <textarea
              className={`${FIELD} min-h-[120px] resize-y`}
              required
              maxLength={1500}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="How can we help?"
            />
          </label>
          <button
            type="submit"
            className="mt-1 inline-flex h-11 items-center justify-center gap-2 rounded-full border-0 bg-[#047857] px-6 text-[15px] font-bold text-white transition-colors hover:bg-[#065f46]"
          >
            Continue to email
          </button>
        </form>
      </div>
    </div>
  );
}
